import { getLanguageInfo } from '@/i18n';

// Country flag rendered via the `flag-icons` SVG sprite (industry standard;
// emoji regional indicators don't render on Windows). The CSS is imported
// once globally in main.tsx, so this component just spits out the right
// `<span class="fi fi-xx">`.
//
// Two ways to call it:
//   <Flag languageCode="hr" />   // looks up the country from SUPPORTED_LANGUAGES
//   <Flag country="gb" />        // pass ISO 3166-1 alpha-2 directly
//
// We also keep a fallback mapping below for languages that exist in
// `WORLD_LANGUAGES` (lib/translation.ts) but not yet in `SUPPORTED_LANGUAGES`
// — chat/booking screens let landlords pick from the wider 95+ list and we
// still want a flag for those rows.

// language code → ISO 3166-1 alpha-2 country code where they differ.
// Codes not listed here use `code === country` (e.g. de, fr, it, pl ...).
const LANG_TO_COUNTRY_FALLBACK: Record<string, string> = {
  en: 'gb', cs: 'cz', sl: 'si', sv: 'se', da: 'dk', el: 'gr',
  zh: 'cn', 'zh-tw': 'tw', ja: 'jp', ko: 'kr', ar: 'sa', he: 'il',
  vi: 'vn', uk: 'ua', sr: 'rs', bs: 'ba', be: 'by', sq: 'al',
  hi: 'in', bn: 'bd', fa: 'ir', ms: 'my', tl: 'ph', sw: 'ke',
  am: 'et', ur: 'pk', af: 'za', hy: 'am', az: 'az', my: 'mm',
  ka: 'ge', gu: 'in', jv: 'id', yo: 'ng', kn: 'in', kk: 'kz',
  ky: 'kg', km: 'kh', lo: 'la', ml: 'in', mr: 'in', mn: 'mn',
  ne: 'np', pa: 'in', si: 'lk', so: 'so', ta: 'in', te: 'in',
  uz: 'uz', zu: 'za', as: 'in', bh: 'in', ceb: 'ph', dv: 'mv',
  ee: 'gh', fj: 'fj', ha: 'ng', hmn: 'la', ig: 'ng', iu: 'ca',
  ga: 'ie',
};

type FlagSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_CLASSES: Record<FlagSize, string> = {
  // flag-icons SVGs are 4:3, so width ~= height * 1.33. We size by width
  // and let the library handle the aspect ratio.
  sm: 'w-4',   // ~16px wide
  md: 'w-5',   // ~20px
  lg: 'w-6',   // ~24px
  xl: 'w-8',   // ~32px
};

interface FlagProps {
  /** Resolve the country from our language registry (preferred). */
  languageCode?: string;
  /** ISO 3166-1 alpha-2 country code, lowercase. Used if languageCode is absent. */
  country?: string;
  size?: FlagSize;
  className?: string;
  /** Accessibility label; defaults to the language name. */
  title?: string;
}

export function Flag({
  languageCode,
  country,
  size = 'md',
  className = '',
  title,
}: FlagProps) {
  let iso = country?.toLowerCase();
  let label = title;

  if (!iso && languageCode) {
    const lc = languageCode.toLowerCase();
    const info = getLanguageInfo(languageCode);
    iso = info?.country ?? LANG_TO_COUNTRY_FALLBACK[lc] ?? lc;
    label ??= info?.name;
  }

  // Unknown code — render a neutral globe-ish placeholder so layout doesn't
  // jump. Better than nothing or a broken sprite reference.
  if (!iso) {
    return (
      <span
        aria-label={label ?? 'flag'}
        className={`inline-block ${SIZE_CLASSES[size]} aspect-4/3 rounded-sm bg-linear-to-br from-cyan-400 to-blue-500 ${className}`}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={label ?? iso.toUpperCase()}
      className={`fi fi-${iso} ${SIZE_CLASSES[size]} aspect-4/3 rounded-sm shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] ${className}`}
    />
  );
}

export default Flag;
